/**
 * Expiry Warning Component
 * Shows warning if batch is expiring soon or expired
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, AlertTriangle, Clock } from 'lucide-react';
import { calculateDaysUntilExpiry, isExpired, isExpiringSoon } from '@/types/inventory';
import { cn } from '@/lib/utils';

interface ExpiryWarningProps {
  expiryDate: string;
  className?: string;
}

export function ExpiryWarning({ expiryDate, className }: ExpiryWarningProps) {
  const daysUntilExpiry = calculateDaysUntilExpiry(expiryDate);
  const expired = isExpired(expiryDate);
  const expiringSoon = isExpiringSoon(expiryDate);

  if (expired) {
    return (
      <Alert className={cn('border-red-300 bg-red-50', className)}>
        <AlertCircle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-800">
          <strong>Expired:</strong> This batch expired {Math.abs(daysUntilExpiry)} days ago.
          It should be quarantined or disposed of.
        </AlertDescription>
      </Alert>
    );
  }

  if (expiringSoon) {
    return (
      <Alert className={cn('border-orange-300 bg-orange-50', className)}>
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <AlertDescription className="text-orange-800">
          <strong>Expiring Soon:</strong> This batch will expire in {daysUntilExpiry} days.
          Priority usage recommended.
        </AlertDescription>
      </Alert>
    );
  }

  if (daysUntilExpiry <= 60) {
    return (
      <Alert className={cn('border-blue-300 bg-blue-50', className)}>
        <Clock className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          This batch will expire in {daysUntilExpiry} days.
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}
